/**
 * Baseball/Softball Position Recommendation Rules
 * 
 * Defines position recommendations for Baseball/Softball players based on their attributes
 */

import type { PositionRule } from '../positionRecommendations';

/**
 * Baseball/Softball Position Rules
 */
export const BASEBALL_POSITION_RULES: PositionRule[] = [
  // PREMIUM POSITIONS - Require elevated thresholds
  {
    position: 'SS',
    tier: 'premium',
    description: 'Shortstop - Most demanding infield position',
    requirements: [
      { attribute: 'glove', minValue: 5, required: true },
      { attribute: 'throwing_accuracy', minValue: 5, required: true },
      { attribute: 'speed', minValue: 4, required: false },
      { attribute: 'agility', minValue: 4, required: false },
    ],
    scoreFormula: (attrs) => {
      const infielding = attrs.get('infielding') || 0;
      const glove = attrs.get('glove') || 0;
      const speed = attrs.get('speed') || 0;
      const agility = attrs.get('agility') || 0;
      const throwingAccuracy = attrs.get('throwing_accuracy') || 0;
      return infielding * 1.5 + glove * 1.2 + (speed + agility) * 1.0 + throwingAccuracy * 1.0;
    },
  },
  {
    position: 'CF',
    tier: 'premium',
    description: 'Center Field - Most important outfield position',
    requirements: [
      { attribute: 'glove', minValue: 5, required: true },
      { attribute: 'speed', minValue: 4, required: true },
    ],
    scoreFormula: (attrs) => {
      const outfielding = attrs.get('outfielding') || 0;
      const glove = attrs.get('glove') || 0;
      const speed = attrs.get('speed') || 0;
      const agility = attrs.get('agility') || 0;
      return outfielding * 1.5 + glove * 1.2 + speed * 1.2 + agility * 1.0;
    },
  },
  {
    position: 'P',
    tier: 'premium',
    description: 'Pitcher - Requires control and power',
    requirements: [
      { attribute: 'pitching', minValue: 5, required: false },
      { attribute: 'throwing_power', minValue: 5, required: false },
      { attribute: 'throwing_accuracy', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const pitching = attrs.get('pitching') || 0;
      const throwingPower = attrs.get('throwing_power') || 0;
      const throwingAccuracy = attrs.get('throwing_accuracy') || 0;
      if (pitching >= 5) {
        return pitching * 2.5 + throwingPower * 0.8 + throwingAccuracy * 0.8;
      }
      return (throwingPower + throwingAccuracy) * 1.2;
    },
  },
  {
    position: 'C',
    tier: 'premium',
    description: 'Catcher - Requires good glove and accurate arm',
    requirements: [
      { attribute: 'catching', minValue: 5, required: false },
      { attribute: 'throwing_accuracy', minValue: 5, required: false },
      { attribute: 'glove', minValue: 5, required: false },
    ],
    scoreFormula: (attrs) => {
      const catching = attrs.get('catching') || 0;
      const throwingAccuracy = attrs.get('throwing_accuracy') || 0;
      const glove = attrs.get('glove') || 0;
      if (catching >= 5) {
        return catching * 2.5 + throwingAccuracy * 1.2 + glove * 1.0;
      }
      return throwingAccuracy * 1.5 + glove * 1.2;
    },
  },
  {
    position: '3B',
    tier: 'premium',
    description: 'Third Base - Needs strong arm and good glove',
    requirements: [
      { attribute: 'glove', minValue: 5, required: true },
      { attribute: 'throwing_power', minValue: 5, required: true },
    ],
    scoreFormula: (attrs) => {
      const infielding = attrs.get('infielding') || 0;
      const glove = attrs.get('glove') || 0;
      const throwingPower = attrs.get('throwing_power') || 0;
      return infielding * 1.5 + glove * 1.2 + throwingPower * 1.2;
    },
  },

  // STANDARD POSITIONS - Moderate thresholds
  {
    position: 'RF',
    tier: 'standard',
    description: 'Right Field - Needs arm strength and glove',
    requirements: [
      { attribute: 'glove', minValue: 4, required: true },
      { attribute: 'throwing_power', minValue: 4, required: true },
    ],
    scoreFormula: (attrs) => {
      const outfielding = attrs.get('outfielding') || 0;
      const glove = attrs.get('glove') || 0;
      const throwingPower = attrs.get('throwing_power') || 0;
      return outfielding * 1.3 + glove * 1.1 + throwingPower * 1.1;
    },
  },
  {
    position: '2B',
    tier: 'standard',
    description: 'Second Base - Needs range and glove for double plays',
    requirements: [
      { attribute: 'glove', minValue: 4, required: true },
      { attribute: 'speed', minValue: 3, required: false },
      { attribute: 'agility', minValue: 3, required: false },
    ],
    scoreFormula: (attrs) => {
      const infielding = attrs.get('infielding') || 0;
      const glove = attrs.get('glove') || 0;
      const speed = attrs.get('speed') || 0;
      const agility = attrs.get('agility') || 0;
      return infielding * 1.3 + glove * 1.1 + (speed + agility) * 0.9;
    },
  },

  // EASIER POSITIONS - Lower thresholds (accessible for less developed players)
  {
    position: '1B',
    tier: 'easier',
    description: 'First Base - Primarily needs glove for picks/receives',
    requirements: [
      { attribute: 'glove', minValue: 3, required: true },
    ],
    scoreFormula: (attrs) => {
      const glove = attrs.get('glove') || 0;
      const infielding = attrs.get('infielding') || 0;
      return glove * 1.8 + infielding * 1.0;
    },
  },
  {
    position: 'LF',
    tier: 'easier',
    description: 'Left Field - Needs glove for catching fly balls (good for youth ball)',
    requirements: [
      { attribute: 'glove', minValue: 3, required: true },
    ],
    scoreFormula: (attrs) => {
      const outfielding = attrs.get('outfielding') || 0;
      const glove = attrs.get('glove') || 0;
      return outfielding * 1.3 + glove * 1.2;
    },
  },
];

